import { useEffect, useState } from "react";
import {
  Button,
  Group,
  Stack,
  Select,
  Modal,
  Loader,
  Center,
  Text,
  Code,
} from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { getPimAttributeDefinitions } from "../Pim/pimApi";

export default function RuleEditor({
  opened,
  onClose,
  onSave,
  sourceSchemaId,
  targetSchemaId,
  existingRule,
}) {
  const { t } = useTranslation("imports");
  const operationTypes = [
    { value: "GetAttribute", label: t("mappings.ruleEditor.opGetAttribute") },
    { value: "ToUpper", label: t("mappings.ruleEditor.opToUpper") },
    { value: "ToLower", label: t("mappings.ruleEditor.opToLower") },
    { value: "Trim", label: t("mappings.ruleEditor.opTrim") },
    { value: "Concat", label: t("mappings.ruleEditor.opConcat") },
  ];
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sourceAttributes, setSourceAttributes] = useState([]);
  const [targetAttributes, setTargetAttributes] = useState([]);

  const [destinationAttributeId, setDestinationAttributeId] = useState("");
  const [operationType, setOperationType] = useState("GetAttribute");
  const [sourceAttribute, setSourceAttribute] = useState("");

  useEffect(() => {
    if (!opened) return;

    const loadAttributes = async () => {
      try {
        setLoading(true);

        const [sourceAttrs, targetAttrs] = await Promise.all([
          sourceSchemaId ? getPimAttributeDefinitions(sourceSchemaId) : [],
          targetSchemaId ? getPimAttributeDefinitions(targetSchemaId) : [],
        ]);

        setSourceAttributes(sourceAttrs || []);
        setTargetAttributes(targetAttrs || []);

        // If editing existing rule, populate the form
        if (existingRule) {
          setDestinationAttributeId(existingRule.destinationAttributeId || "");
          // Extract operation type and source attribute from pipeline
          const pipeline = existingRule.pipeline;
          if (pipeline?.operation) {
            const op = pipeline.operation;
            if (op.type === "GetAttribute" && op.args?.length > 0) {
              setOperationType("GetAttribute");
              setSourceAttribute(op.args[0]);
            } else if (["ToUpper", "ToLower", "Trim"].includes(op.type)) {
              setOperationType(op.type);
              // Nested operation - extract inner GetAttribute
              if (op.args?.length > 0 && typeof op.args[0] === "object") {
                const innerOp = op.args[0];
                if (innerOp.type === "GetAttribute" && innerOp.args?.length > 0) {
                  setSourceAttribute(innerOp.args[0]);
                }
              }
            } else {
              setOperationType("GetAttribute");
              setSourceAttribute("");
            }
          }
        } else {
          setDestinationAttributeId("");
          setOperationType("GetAttribute");
          setSourceAttribute("");
        }
      } catch {
        notifications.show({
          color: "red",
          title: t("mappings.common.errorTitle"),
          message: t("mappings.ruleEditor.attributesLoadFailed"),
        });
      } finally {
        setLoading(false);
      }
    };

    loadAttributes();
  }, [opened, sourceSchemaId, targetSchemaId, existingRule]);

  const buildPipeline = () => {
    const getAttributeOp = {
      type: "GetAttribute",
      args: [sourceAttribute],
    };

    if (operationType === "GetAttribute") {
      return { operation: getAttributeOp };
    }

    // For ToUpper, ToLower, Trim - wrap GetAttribute
    return {
      operation: {
        type: operationType,
        args: [getAttributeOp],
      },
    };
  };

  const formatPipelinePreview = () => {
    const src = sourceAttribute || "?";
    if (operationType === "GetAttribute") {
      return `GetAttribute(${src})`;
    }
    return `${operationType}(GetAttribute(${src}))`;
  };

  const handleSave = async () => {
    if (!destinationAttributeId) {
      notifications.show({
        color: "red",
        title: t("mappings.ruleEditor.validationTitle"),
        message: t("mappings.ruleEditor.destinationRequired"),
      });
      return;
    }

    if (!sourceAttribute) {
      notifications.show({
        color: "red",
        title: t("mappings.ruleEditor.validationTitle"),
        message: t("mappings.ruleEditor.sourceRequired"),
      });
      return;
    }

    try {
      setSaving(true);

      const rule = {
        destinationAttributeId,
        pipeline: buildPipeline(),
      };

      await onSave(rule);
      onClose();
    } catch {
      notifications.show({
        color: "red",
        title: t("mappings.common.errorTitle"),
        message: t("mappings.ruleEditor.saveFailed"),
      });
    } finally {
      setSaving(false);
    }
  };

  const sourceOptions = sourceAttributes.map((attr) => ({
    value: attr.code,
    label: attr.labels?.en || attr.code,
  }));

  const targetOptions = targetAttributes.map((attr) => ({
    value: attr.id,
    label: attr.labels?.en || attr.code,
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        existingRule
          ? t("mappings.ruleEditor.editTitle")
          : t("mappings.ruleEditor.addTitle")
      }
      size="md"
    >
      {loading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : (
        <Stack gap="md">
          <Select
            label={t("mappings.ruleEditor.destinationLabel")}
            description={t("mappings.ruleEditor.destinationDescription")}
            placeholder={t("mappings.ruleEditor.destinationPlaceholder")}
            data={targetOptions}
            value={destinationAttributeId}
            onChange={setDestinationAttributeId}
            searchable
            required
          />

          <Select
            label={t("mappings.ruleEditor.operationLabel")}
            description={t("mappings.ruleEditor.operationDescription")}
            data={operationTypes}
            value={operationType}
            onChange={setOperationType}
            required
          />

          <Select
            label={t("mappings.ruleEditor.sourceLabel")}
            description={t("mappings.ruleEditor.sourceDescription")}
            placeholder={t("mappings.ruleEditor.sourcePlaceholder")}
            data={sourceOptions}
            value={sourceAttribute}
            onChange={setSourceAttribute}
            searchable
            required
          />

          <div>
            <Text size="sm" c="dimmed" mb="xs">
              {t("mappings.ruleEditor.pipelinePreview")}
            </Text>
            <Code block>{formatPipelinePreview()}</Code>
          </div>

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              {t("common:generic.action_cancel")}
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              loading={saving}
            >
              {existingRule
                ? t("mappings.ruleEditor.update")
                : t("mappings.profile.addRule")}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
